import React from "react";

export interface Slice {
  key: string;
  label: string;
  value: number;
  color: string;
}

export const percent = (value: number, total: number) =>
  total ? `${((value / total) * 100).toFixed(1)}%` : "0%";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

// 0 → 1 over `duration` ms, restarted whenever `replayKey` changes, so a
// chart builds up again after a refresh or a new date range.
const useProgress = (replayKey: string, duration = 1000) => {
  const [progress, setProgress] = React.useState(prefersReducedMotion() ? 1 : 0);

  React.useEffect(() => {
    if (prefersReducedMotion()) {
      setProgress(1);
      return;
    }
    setProgress(0);
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      setProgress(easeOut(t));
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [replayKey, duration]);

  return progress;
};

// Counts up from the previously shown value to the new one.
export const CountUp: React.FC<{ value: number; duration?: number }> = ({
  value,
  duration = 900,
}) => {
  const [shown, setShown] = React.useState(prefersReducedMotion() ? value : 0);
  const shownRef = React.useRef(shown);

  React.useEffect(() => {
    if (prefersReducedMotion()) {
      shownRef.current = value;
      setShown(value);
      return;
    }
    const from = shownRef.current;
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const next = Math.round(from + (value - from) * easeOut(t));
      shownRef.current = next;
      setShown(next);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return <>{shown.toLocaleString()}</>;
};

export const StageDonut: React.FC<{
  slices: Slice[];
  total: number;
  centerLabel: string;
}> = ({ slices, total, centerLabel }) => {
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const progress = useProgress(slices.map((s) => `${s.key}:${s.value}`).join("|"));
  // The ring sweeps round clockwise: every slice is drawn at `progress` of
  // its length and position.
  const swept = circumference * progress;
  let offset = 0;

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row">
      <svg viewBox="0 0 180 180" className="h-40 w-40 shrink-0">
        <g transform="rotate(-90 90 90)">
          <circle cx="90" cy="90" r={radius} fill="none" stroke="#f3f4f6" strokeWidth="28" />
          {total > 0 &&
            slices.map((slice) => {
              const full = (slice.value / total) * circumference;
              const length = Math.max(0, Math.min(full, swept - offset));
              const dash = (
                <circle
                  key={slice.key}
                  cx="90"
                  cy="90"
                  r={radius}
                  fill="none"
                  stroke={slice.color}
                  strokeWidth="28"
                  strokeDasharray={`${length} ${circumference - length}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += full;
              return dash;
            })}
        </g>
        <text x="90" y="88" textAnchor="middle" className="fill-gray-900 text-[28px] font-bold">
          <CountUp value={total} />
        </text>
        <text x="90" y="110" textAnchor="middle" className="fill-gray-500 text-[11px]">
          {centerLabel}
        </text>
      </svg>
      <ul className="min-w-0 flex-1 space-y-2 text-sm">
        {slices.map((slice) => (
          <li key={slice.key} className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2 text-gray-700">
              <span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: slice.color }} />
              <span className="truncate" title={slice.label}>
                {slice.label}
              </span>
            </span>
            <span className="shrink-0 whitespace-nowrap font-semibold text-gray-900">
              <CountUp value={slice.value} />{" "}
              <span className="font-normal text-gray-500">({percent(slice.value, total)})</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const TrendChart: React.FC<{
  points: { label: string; received: number; closed: number }[];
  receivedLabel: string;
  closedLabel: string;
}> = ({ points, receivedLabel, closedLabel }) => {
  const width = 420;
  const height = 200;
  const pad = { top: 12, right: 16, bottom: 28, left: 32 };
  const max = Math.max(4, ...points.map((p) => Math.max(p.received, p.closed)));
  const top = Math.ceil(max / 4) * 4;
  const x = (i: number) =>
    pad.left + (points.length > 1 ? (i * (width - pad.left - pad.right)) / (points.length - 1) : 0);
  const y = (v: number) => pad.top + (1 - v / top) * (height - pad.top - pad.bottom);
  const line = (key: "received" | "closed") =>
    points.map((p, i) => `${i ? "L" : "M"}${x(i)},${y(p[key])}`).join(" ");
  const ticks = [0, top / 4, top / 2, (3 * top) / 4, top];
  const progress = useProgress(
    points.map((p) => `${p.label}:${p.received}:${p.closed}`).join("|"),
    1400,
  );
  // A point appears once the line being drawn has reached it.
  const reached = (i: number) =>
    points.length < 2 ? progress > 0 : progress >= i / (points.length - 1) - 0.001;

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-52 w-full">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke="#f3f4f6" />
            <text x={pad.left - 8} y={y(t) + 4} textAnchor="end" className="fill-gray-400 text-[10px]">
              {t}
            </text>
          </g>
        ))}
        {points.map((p, i) => (
          <text key={p.label} x={x(i)} y={height - 8} textAnchor="middle" className="fill-gray-500 text-[10px]">
            {p.label}
          </text>
        ))}
        {(["received", "closed"] as const).map((key) => {
          const color = key === "received" ? "#991b1b" : "#16a34a";
          return (
            <g key={key}>
              <path
                d={line(key)}
                fill="none"
                stroke={color}
                strokeWidth="2.5"
                pathLength={1}
                strokeDasharray="1"
                strokeDashoffset={1 - progress}
              />
              {points.map((p, i) => (
                <circle
                  key={i}
                  cx={x(i)}
                  cy={y(p[key])}
                  r={reached(i) ? 4 : 0}
                  fill="white"
                  stroke={color}
                  strokeWidth="2"
                  style={{ transition: "r 200ms ease-out" }}
                >
                  <title>{`${p.label}: ${p[key]}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
      <div className="mt-2 flex gap-6 text-sm text-gray-600">
        <span className="flex items-center gap-2">
          <span className="h-0.5 w-5 bg-[#991b1b]" /> {receivedLabel}
        </span>
        <span className="flex items-center gap-2">
          <span className="h-0.5 w-5 bg-[#16a34a]" /> {closedLabel}
        </span>
      </div>
    </div>
  );
};

const BAR_COLORS = ["#991b1b", "#b91c1c", "#ea580c", "#f59e0b", "#4b5563", "#9ca3af"];

export const CountBars: React.FC<{
  rows: { label: string; count: number }[];
  emptyText: string;
}> = ({ rows, emptyText }) => {
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  const progress = useProgress(rows.map((r) => `${r.label}:${r.count}`).join("|"));
  if (!rows.length) {
    return <p className="py-8 text-center text-sm text-gray-500">{emptyText}</p>;
  }
  return (
    <ul className="space-y-4">
      {rows.map((row, i) => (
        <li key={row.label}>
          <div className="mb-1 flex justify-between gap-3 text-sm">
            <span className="truncate text-gray-700" title={row.label}>
              {row.label}
            </span>
            <span className="shrink-0 font-semibold text-gray-900">
              <CountUp value={row.count} /> ({percent(row.count, total)})
            </span>
          </div>
          <div className="h-2 rounded-full bg-gray-100">
            <div
              className="h-2 rounded-full"
              style={{
                width: `${total ? (row.count / total) * 100 * progress : 0}%`,
                backgroundColor: BAR_COLORS[i % BAR_COLORS.length],
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};
